import React, { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { selectors as appSelectors } from 'userportal/ducks/app';
import { selectors as authSelectors } from 'userportal/ducks/auth';
import ErrorBoundary from 'shared/components/ErrorBoundary';
import FallbackScreen from 'shared/components/ErrorBoundary/FallbackScreen';
import Loadable from 'shared/components/Loadable';
import WsContextProvider from 'shared/contexts/WsContextProvider';
import Layout from 'userportal/components/Layout';
import PublicOnlyRoute from 'userportal/components/PublicOnlyRoute';
import LazyProtectedRoute from 'userportal/components/LazyProtectedRoute';
import Footer from 'userportal/components/Footer';
import * as Pages from 'userportal/pages';
import apiClient from 'userportal/lib/ApiClient';
import { userportal as routes } from 'shared/routes';
import config from '../../config';

const loadUserPages = () => import('userportal/pages/User');
const LazyProfile      = lazy(() => loadUserPages().then(m => ({ default: m.default.Profile })));
const LazyApplications = lazy(() => loadUserPages().then(m => ({ default: m.default.Applications })));
const LazyApplication  = lazy(() => loadUserPages().then(m => ({ default: m.default.Application })));
const LazyOffers       = lazy(() => loadUserPages().then(m => ({ default: m.default.Offers })));
const LazyOffer        = lazy(() => loadUserPages().then(m => ({ default: m.default.Offer })));
const LazyFavoriteJobs = lazy(() => loadUserPages().then(m => ({ default: m.default.FavoriteJobs })));
const LazySettings     = lazy(() => loadUserPages().then(m => ({ default: m.default.Settings })));

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const App = () => {
  const { loggingIn, loggingOut } = useSelector(authSelectors.getFlags);
  const { appLoading, appLoadingFailed, appLoadingError } = useSelector(appSelectors.getFlags);

  // const navigationExists = !!document.getElementById('navigation');

  return !appLoadingFailed ? (
    <ErrorBoundary>
      <WsContextProvider
        config={config}
        apiClient={apiClient}
        authStateSelector={authSelectors.getState}
      >
        <Layout>
          <Loadable
            loading={loggingIn || loggingOut || appLoading}
            // loading={loggingIn || loggingOut || (appLoading && !navigationExists)}
            screen={Pages.Loading}
            render={() => (
              <Suspense fallback={null}>
              <SentryRoutes>
                <Route path={routes.login} element={<PublicOnlyRoute component={Pages.Login} />} />
                <Route path={routes.signup} element={<PublicOnlyRoute component={Pages.Signup} />} />
                <Route path={routes.invitation} element={<PublicOnlyRoute component={Pages.Invitation} />} />
                <Route path={routes.resetPassword} element={<PublicOnlyRoute component={Pages.PasswordReset} />} />
                <Route path={routes.resetPasswordLink} element={<PublicOnlyRoute component={Pages.PasswordReset} />} />

                <Route path={routes.oauth.signup} element={<Pages.Oauth.Signup />} />
                <Route path={routes.oauth.linkedIn.login} element={<Pages.Oauth.LinkedIn.Request />} />
                <Route path={routes.oauth.linkedIn.callbackUrl} element={<Pages.Oauth.LinkedIn.Callback />} />
                <Route path={routes.oauth.google.login} element={<Pages.Oauth.Google.Request />} />
                <Route path={routes.oauth.google.callbackUrl} element={<Pages.Oauth.Google.Callback />} />

                <Route path={routes.job.detail} element={<Pages.JobDetail />} />
                <Route path={routes.company.detail} element={<Pages.Company />} />

                <Route path={routes.user.applications} element={<LazyProtectedRoute component={LazyApplications} />} />
                <Route path={routes.user.application} element={<LazyProtectedRoute component={LazyApplication} />} />
                <Route path={routes.user.offers} element={<LazyProtectedRoute component={LazyOffers} />} />
                <Route path={routes.user.offer} element={<LazyProtectedRoute component={LazyOffer} />} />
                <Route path={routes.user.settings} element={<LazyProtectedRoute component={LazySettings} />} />
                <Route path={routes.user.favoriteJobs} element={<LazyProtectedRoute component={LazyFavoriteJobs} />} />
                <Route path={`${routes.user.profile.view}/*`} element={<LazyProtectedRoute component={LazyProfile} />} />

                <Route path={routes.howItWorks} element={<Pages.HowItWorks />} />
                <Route path={routes.about} element={<Pages.About />} />
                <Route path={routes.contact} element={<Pages.Contact />} />
                <Route path={routes.tos} element={<Pages.Tos />} />
                <Route path={routes.gdpr} element={<Pages.Gdpr />} />
                <Route path="*" element={<Pages.Home />} />
              </SentryRoutes>
              </Suspense>
            )}
          />
          <Footer />
          <ToastContainer
            position="bottom-right"
            style={{ bottom: 72 }}
            autoClose={5000}
            hideProgressBar
            newestOnTop
            closeOnClick={false}
            rtl={false}
            pauseOnFocusLoss
            draggable
            theme="colored"
            pauseOnHover
          />
        </Layout>
      </WsContextProvider>
    </ErrorBoundary>
  ) : (
    <FallbackScreen standalone error={appLoadingError} />
  );
};

export default App;
